/**
 * Utilidades de cookies puras (sin dependencia de plataforma). Sirven igual para
 * `document.cookie` en el navegador que para `request.headers.get('cookie')` en
 * SSR, así que un mismo valor persiste de forma que el servidor SSR también lo
 * lee (a diferencia de `localStorage`, invisible para el server).
 *
 * El valor puede contener JSON (comillas, llaves, `;`), caracteres no válidos en
 * el cuerpo de una cookie: `serializeCookie` lo codifica con `encodeURIComponent`
 * y `readCookie` lo decodifica, así que el round-trip es transparente.
 */

/**
 * Lee el valor de la cookie `name` a partir de un header de cookies
 * (`"a=1; b=2"`). Acepta tanto `document.cookie` como el header entrante de SSR.
 * Devuelve el valor decodificado, o `null` si la cookie no está presente.
 */
export function readCookie(cookieHeader: string | null | undefined, name: string): string | null {
  if (!cookieHeader) return null;
  const target = `${name}=`;
  for (const part of cookieHeader.split(';')) {
    const cookie = part.trim();
    if (cookie.startsWith(target)) {
      try {
        return decodeURIComponent(cookie.slice(target.length));
      } catch {
        // Valor mal formado (decode inválido): trátalo como ausente.
        return null;
      }
    }
  }
  return null;
}

export interface CookieOptions {
  /** Vida de la cookie en segundos (`Max-Age`). Sin él, es de sesión. */
  maxAgeSeconds?: number;
  /** Ruta de la cookie. Por defecto `/`. */
  path?: string;
  /** Política SameSite. Por defecto `Lax` (basta para navegación top-level). */
  sameSite?: 'Lax' | 'Strict' | 'None';
  /** Añade el flag `Secure` (obligatorio con `SameSite=None` y en https). */
  secure?: boolean;
}

/**
 * Construye el string de una cookie listo para asignar a `document.cookie`.
 * Codifica el valor con `encodeURIComponent`.
 */
export function serializeCookie(name: string, value: string, opts: CookieOptions = {}): string {
  const parts = [`${name}=${encodeURIComponent(value)}`];
  if (opts.maxAgeSeconds != null) parts.push(`Max-Age=${opts.maxAgeSeconds}`);
  parts.push(`Path=${opts.path ?? '/'}`);
  parts.push(`SameSite=${opts.sameSite ?? 'Lax'}`);
  if (opts.secure) parts.push('Secure');
  return parts.join('; ');
}
